import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardCheck, Camera, Send, Award, Sparkles } from "lucide-react";

// Onboarding slides — tailored per role. Workers get shop-floor tips;
// architects/managers get project-workflow tips.
const SLIDES_WORKER = [
  {
    icon: LayoutDashboard,
    title: "Hoş Geldin! 👋",
    body: "AKB Karavan üretim uygulamasına ilk girişin. Sana kısaca uygulamayı anlatacağım — sadece 4 adım.",
  },
  {
    icon: ClipboardCheck,
    title: "1. İşlerin Buradadır",
    body: "Sol menüde kendi bölümüne tıkla. Sana atanmış araçları göreceksin: sırası (#1, #2…), teslim tarihi ve o araç için yapılacak ekipman listesi.",
  },
  {
    icon: Camera,
    title: "2. Fotoğraf & Detay Ekle",
    body: "Bir aracın kartına tıkla → 'Fotoğraf Yükle' ile yaptığın işi belgeleyebilir, notlarını ekleyebilirsin.",
  },
  {
    icon: Send,
    title: "3. Bittiğinde 'Onaya Sun'",
    body: "İşi tamamlayınca 'Onaya Sun' butonuna bas. Bölüm şefin kontrol edecek, sonra nihai onay verilecek.",
  },
  {
    icon: Award,
    title: "4. Puanlarını Topla 🏆",
    body: "Her onaylanan iş sana puan kazandırır. Aylık puanların bordronda görülecek. Reddedilirse revizyon yaparsın — takım güçlensin diye.",
  },
];

const SLIDES_MANAGER = [
  {
    icon: LayoutDashboard,
    title: "Hoş Geldin! 👋",
    body: "AKB Karavan yönetim panelinde ilk girişin. Kısaca ana ekranları göstereceğim.",
  },
  {
    icon: Sparkles,
    title: "Kontrol Paneli",
    body: "Aktif üretim, geciken teslimatlar, kritik stok ve son fotoğraf yüklemeleri buradadır. Her araç için 9-basamaklı aşama grafiği görürsün.",
  },
  {
    icon: ClipboardCheck,
    title: "Araçlar & Otomatik Dağıtım",
    body: "Yeni bir araç eklediğinde 8 bölüme otomatik olarak iş emri düşer. Paket + ekstra ekipmanları toplayıp doğru bölüme yönlendirir.",
  },
  {
    icon: Award,
    title: "Onay Zinciri",
    body: "Bölüm şefleri işleri onaylar → sen (veya İşletme Müdürü) nihai onayı verirsin. Onaylanan işler puan üretir ve bordroya işlenir.",
  },
];

const SLIDES_ARCHITECT = [
  {
    icon: LayoutDashboard,
    title: "Hoş Geldin Mimar! 🎨",
    body: "AKB'de her aracın ilk startını sen veriyorsun. İşin başlamadan araç senden geçer.",
  },
  {
    icon: ClipboardCheck,
    title: "Araç Startı",
    body: "Her yeni araca zorunlu olarak teknik çizim + 3 adet 3D render + notlar + ekstra donanım seçimi ekleyeceksin. Sen onaylayınca üretim başlar.",
  },
  {
    icon: Sparkles,
    title: "Erişim Alanların",
    body: "Araç sırası, teslim tarihi, üretim takvimi, aşama onayları — hepsi elinde. Anlaşma rakamları ve finansal veri sadece admin'de görünür.",
  },
];

function slidesFor(user) {
  if (user?.role === "architect") return SLIDES_ARCHITECT;
  const workerRoles = ["production", "worker", "department_chief"];
  if (workerRoles.includes(user?.role)) return SLIDES_WORKER;
  return SLIDES_MANAGER;
}

export default function OnboardingModal({ open, onClose }) {
  const { user, setUser } = useAuth();
  const [idx, setIdx] = useState(0);
  const slides = slidesFor(user);
  const isLast = idx === slides.length - 1;
  const S = slides[idx];
  const Icon = S?.icon || Sparkles;

  const finish = async () => {
    try {
      await api.post("/auth/onboarding-done");
      setUser({ ...user, onboarding_done: true });
    } catch { /* ignore */ }
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-2 border-emerald-300 dark:border-emerald-800"
        data-testid="onboarding-dialog"
      >
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pt-6 pb-2">
          {slides.map((_, i) => (
            <span key={i} className={`w-2 h-2 rounded-full transition-all ${
              i === idx ? "w-6 bg-emerald-500" : i < idx ? "bg-emerald-300" : "bg-slate-300"
            }`} />
          ))}
        </div>

        <div className="px-8 pt-4 pb-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 text-white flex items-center justify-center mb-5 shadow-md">
            <Icon className="w-8 h-8" />
          </div>
          <DialogTitle className="font-display text-xl font-bold mb-3">{S?.title}</DialogTitle>
          <p className="text-sm text-muted-foreground leading-relaxed">{S?.body}</p>
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-3 bg-muted/30">
          <Button
            variant="ghost" size="sm"
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            data-testid="onboarding-prev"
          >
            Geri
          </Button>
          <span className="text-xs text-muted-foreground font-mono">
            {idx + 1} / {slides.length}
          </span>
          {isLast ? (
            <Button size="sm" onClick={finish} data-testid="onboarding-finish">
              Anladım, Başlayalım 🚀
            </Button>
          ) : (
            <Button size="sm" onClick={() => setIdx(idx + 1)} data-testid="onboarding-next">
              Devam
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
